import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Twitter, Youtube, Linkedin, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reasons = [
  "General Inquiry",
  "Business Development",
  "Strategic Partnership",
  "Media & Content",
  "ADD VALUE Clothing",
  "Other",
];

const socials = [
  { icon: Instagram, label: "Instagram", id: "instagram", href: "https://www.instagram.com/value_clothing_co/" },
  { icon: Twitter, label: "X (Twitter)", id: "twitter", href: null },
  { icon: Youtube, label: "YouTube", id: "youtube", href: null },
  { icon: Linkedin, label: "LinkedIn", id: "linkedin", href: null },
];

const emptyForm = { name: "", email: "", phone: "", company: "", reason: "", message: "" };

const inputCls =
  "w-full border border-[#2a2a2e] bg-[#121214] px-5 py-4 text-sm text-white placeholder:text-stone-600 transition-colors duration-300 focus:border-[#D4AF37] focus:outline-none";

const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Contact — ADD VALUE HOLDINGS LLC";
  }, []);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll be in touch.");
      setForm(emptyForm);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        testid="contact-hero"
        overline="Contact"
        lines={["Start the", "conversation."]}
        lede="Business inquiries, partnerships, media, and everything in between — this is the front door of ADD VALUE HOLDINGS LLC."
      />

      <section className="border-t border-[#1c1c1f]">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 sm:px-8 lg:grid-cols-12 lg:px-12 lg:py-32">
          <Reveal className="lg:col-span-5">
            <Overline>Direct Line</Overline>
            <h2 className="mt-8 font-serif text-2xl tracking-tight text-white sm:text-3xl">
              ADD VALUE HOLDINGS LLC
            </h2>
            <div className="mt-10 space-y-8">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  Business Email
                </p>
                <a
                  data-testid="contact-email"
                  href="mailto:tionneeduncan@valyouenterprise.com"
                  className="mt-3 block text-base text-stone-300 transition-colors duration-300 hover:text-white"
                >
                  tionneeduncan@valyouenterprise.com
                </a>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  Business Phone
                </p>
                <a
                  data-testid="contact-phone"
                  href="tel:+13073162461"
                  className="mt-3 block text-base text-stone-300 transition-colors duration-300 hover:text-white"
                >
                  +1 (307) 316-2461
                </a>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  Social
                </p>
                <div className="mt-4 flex gap-3">
                  {socials.map((s) => (
                    <a
                      key={s.id}
                      href={s.href || "#"}
                      {...(s.href
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : { onClick: (e) => e.preventDefault() })}
                      data-testid={`contact-social-${s.id}`}
                      aria-label={s.href ? s.label : `${s.label} (coming soon)`}
                      title={s.href ? s.label : "Coming soon"}
                      className="flex h-11 w-11 items-center justify-center border border-[#2a2a2e] text-stone-400 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                    >
                      <s.icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-[#222226] bg-[#0d0d0f] p-8 lg:p-12"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="mb-3 block font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">
                    Name *
                  </label>
                  <input id="cf-name" data-testid="contact-field-name" required value={form.name} onChange={update("name")} placeholder="Full name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-3 block font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">
                    Email *
                  </label>
                  <input id="cf-email" data-testid="contact-field-email" type="email" required value={form.email} onChange={update("email")} placeholder="you@company.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-phone" className="mb-3 block font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">
                    Phone
                  </label>
                  <input id="cf-phone" data-testid="contact-field-phone" value={form.phone} onChange={update("phone")} placeholder="Optional" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-company" className="mb-3 block font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">
                    Company
                  </label>
                  <input id="cf-company" data-testid="contact-field-company" value={form.company} onChange={update("company")} placeholder="Optional" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-reason" className="mb-3 block font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">
                    Reason for Contact *
                  </label>
                  <select
                    id="cf-reason"
                    data-testid="contact-field-reason"
                    required
                    value={form.reason}
                    onChange={update("reason")}
                    className={`${inputCls} appearance-none ${form.reason ? "text-white" : "text-stone-600"}`}
                  >
                    <option value="" disabled>
                      Select a reason
                    </option>
                    {reasons.map((r) => (
                      <option key={r} value={r} className="bg-[#121214] text-white">
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-message" className="mb-3 block font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">
                    Message *
                  </label>
                  <textarea
                    id="cf-message"
                    data-testid="contact-field-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us what you're building, or what you'd like to discuss."
                    className={`${inputCls} resize-none`}
                  />
                </div>
              </div>
              <button
                data-testid="contact-form-submit-button"
                type="submit"
                disabled={submitting}
                className="group mt-10 inline-flex items-center gap-3 bg-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-[#D4AF37] disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Send Message"}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
